import { backendPost } from '../../common/api/BackendApi.ts';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';

export interface RegistrationOptions {
  email: string;
  firstName: string;
  lastName: string;
}

export interface RegistrationCredential {
  credential: RegistrationResponseJSON;
  email: string;
  firstName: string;
  lastName: string;
}

export interface AuthenticationOptions {
  email: string;
}

export interface AuthenticationCredential {
  credential: AuthenticationResponseJSON;
}

export interface RegistrationOptionsResponse {
  [key: string]: unknown;
}

function convertToWebAuthnOptions(
  backendResponse: RegistrationOptionsResponse
): PublicKeyCredentialCreationOptionsJSON {
  // The backend response is already in the correct format from the webauthn gem
  // We just need to ensure TypeScript knows it's the right type
  return backendResponse as unknown as PublicKeyCredentialCreationOptionsJSON;
}

export async function getRegistrationOptions(
  data: RegistrationOptions
): Promise<PublicKeyCredentialCreationOptionsJSON> {
  // Wrap the data in a registration object and convert our app fields to snake_case
  const payload = {
    registration: {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
    },
  };

  const backendResponse = await backendPost<RegistrationOptionsResponse>(
    '/portal/passkeys/registration_options',
    JSON.stringify(payload),
    {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // establish a session
    }
  );

  return convertToWebAuthnOptions(backendResponse);
}

export async function registerPasskey(
  data: RegistrationCredential
): Promise<void> {
  // Wrap the data in a registration object and convert our app fields to snake_case
  // while preserving WebAuthn credential data in its expected camelCase format
  const payload = {
    registration: {
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      credential: data.credential, // Keep WebAuthn data as-is
    },
  };

  return backendPost<void>(
    '/portal/passkeys/register',
    JSON.stringify(payload),
    {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // use the session we established earlier
    }
  );
}

function convertToWebAuthnAuthOptions(
  backendResponse: RegistrationOptionsResponse
): PublicKeyCredentialRequestOptionsJSON {
  return backendResponse as unknown as PublicKeyCredentialRequestOptionsJSON;
}

export async function getAuthenticationOptions(
  data: AuthenticationOptions
): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const backendResponse = await backendPost<RegistrationOptionsResponse>(
    '/portal/passkeys/authentication_options',
    JSON.stringify(data),
    {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }
  );

  return convertToWebAuthnAuthOptions(backendResponse);
}

export async function authenticatePasskey(
  data: AuthenticationCredential
): Promise<void> {
  return backendPost<void>(
    '/portal/passkeys/authenticate',
    JSON.stringify(data),
    {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }
  );
}
